import React, { useState, useEffect } from 'react';
import {
  Search, Eye, Calendar, User, Fuel, Download, X,
  ChevronLeft, ChevronRight, Loader2, FileText, Printer,
  Pencil, Trash2, Lock, Save, XCircle, MessageCircle
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';
import { jsPDF } from 'jspdf';
import megalogo from '../Image/megalogo.png';
import ActivityFeedModal_Fuel from '../Activity/ActivityFeedModal_Fuel';

export default function FuelRequisitionList() {
  const [fuelRequests, setFuelRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showFeedModal, setShowFeedModal] = useState(false);
  const [feedFuel, setFeedFuel] = useState(null);

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    fetchFuelRequests();
    fetchWebsiteInfo();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [searchTerm, fuelRequests]);

  const fetchWebsiteInfo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Website_Details`);
      if (!res.ok) throw new Error('Failed to fetch website info');
      const data = await res.json();
      const activeWebsite = data.find(w => w.isActive) || data[0];
      if (activeWebsite) setWebsiteInfo(activeWebsite);
    } catch (error) {
      console.error('Error fetching website info:', error);
    }
  };

  const fetchFuelRequests = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/FuelRequisition`);
      if (!response.ok) { setFuelRequests([]); setFilteredRequests([]); return; }
      const data = await response.json();
      const requestArray = Array.isArray(data) ? data : (data ? [data] : []);
      setFuelRequests(requestArray);
      setFilteredRequests(requestArray);
    } catch (error) {
      console.error('❌ Error fetching fuel requests:', error);
      setFuelRequests([]); setFilteredRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...fuelRequests];
    if (searchTerm) {
      filtered = filtered.filter(req =>
        req.controlNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.requestorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.plateNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredRequests(filtered);
    setCurrentPage(1);
  };

  // ✅ Approved kung may laman ang approvedBy field
  const isApproved = (request) => {
    return request.approvedByVisor === true || request.approvedByVisor === 1;
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  // ─── EDIT ───
  const handleEditRequest = (request) => {
    if (isApproved(request)) {
      Swal.fire({
        icon: 'warning',
        title: 'Cannot Edit',
        text: 'This fuel requisition has already been approved and cannot be edited.',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }
    setEditData({ ...request });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editData.requestorName || !editData.plateNumber || !editData.department) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please fill in required fields: Requestor, Plate #, and Department',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    setEditLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/FuelRequisition/${editData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData.errors || errorData));
      }

      // ✅ Log edit success
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "EDIT",
          module: "Fuel Requisition",
          description: `Edited Fuel Requisition #${editData.controlNo} (ID: ${editData.id}) for ${editData.requestorName} - Plate: ${editData.plateNumber}, Dept: ${editData.department}`,
          userId: currentUser?.id || currentUser?.userId,
          username: currentUser?.username,
          fullName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.username,
        }),
      }).catch(err => console.error("Log failed:", err));

      setShowEditModal(false);
      setEditData(null);
      await fetchFuelRequests();

      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        html: `<strong>${editData.controlNo}</strong><br/>Fuel requisition updated successfully`,
        timer: 2000,
        confirmButtonColor: '#10b981'
      });
    } catch (error) {
      console.error('❌ Edit error:', error);

      // ✅ Log edit failed
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "EDIT-FAILED",
          module: "Fuel Requisition",
          description: `Failed to edit Fuel Requisition #${editData.controlNo} (ID: ${editData.id}). Error: ${error.message}`,
          userId: currentUser?.id || currentUser?.userId,
          username: currentUser?.username,
          fullName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.username,
        }),
      }).catch(err => console.error("Log failed:", err));

      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: error.message || 'Failed to update fuel requisition',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setEditLoading(false);
    }
  };

  // ─── DELETE ───
  const handleDeleteRequest = async (request) => {
    if (isApproved(request)) {
      Swal.fire({
        icon: 'warning',
        title: 'Cannot Delete',
        text: 'This fuel requisition has already been approved and cannot be deleted.',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete Fuel Requisition?',
      html: `Are you sure you want to delete <strong>${request.controlNo}</strong>?<br/><span class="text-sm text-gray-500">This action cannot be undone.</span>`,
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}api/FuelRequisition/${request.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error('Failed to delete fuel requisition');

      // ✅ Log delete success
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "DELETE",
          module: "Fuel Requisition",
          description: `Deleted Fuel Requisition #${request.controlNo} (ID: ${request.id}) for ${request.requestorName} - Plate: ${request.plateNumber}, Dept: ${request.department}`,
          userId: currentUser?.id || currentUser?.userId,
          username: currentUser?.username,
          fullName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.username,
        }),
      }).catch(err => console.error("Log failed:", err));

      await fetchFuelRequests();

      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        html: `<strong>${request.controlNo}</strong> has been deleted.`,
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('❌ Delete error:', error);

      // ✅ Log delete failed
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "DELETE-FAILED",
          module: "Fuel Requisition",
          description: `Failed to delete Fuel Requisition #${request.controlNo} (ID: ${request.id}). Error: ${error.message}`,
          userId: currentUser?.id || currentUser?.userId,
          username: currentUser?.username,
          fullName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.username,
        }),
      }).catch(err => console.error("Log failed:", err));

      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: error.message || 'Failed to delete fuel requisition',
        confirmButtonColor: '#dc2626'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  // ─── exportToPDF (unchanged) ───
  const exportToPDF = (request) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 14;
    let y = 14;
    try { doc.addImage(megalogo, 'PNG', margin, y, 25, 13); } catch (err) { }
    doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20);
    doc.text(websiteInfo?.websiteName || 'MEGASOFT HYGIENIC PRODUCTS, INC.', pageWidth / 2, y + 5, { align: 'center' });
    doc.setFontSize(7); doc.setFont(undefined, 'italic'); doc.setTextColor(100, 100, 100);
    doc.text(websiteInfo?.websiteSlogan || 'A MEGASOFT IN EVERY HOME', pageWidth / 2, y + 10, { align: 'center' });
    if (websiteInfo?.websiteContact) doc.text(websiteInfo.websiteContact, pageWidth / 2, y + 14, { align: 'center' });
    doc.setDrawColor(180, 180, 180); doc.setLineWidth(0.3); doc.rect(pageWidth - margin - 42, y, 42, 16);
    doc.setFontSize(6); doc.setFont(undefined, 'normal'); doc.setTextColor(60, 60, 60);
    doc.text('DOCUMENT CODE: SPR-F-009', pageWidth - margin - 40, y + 4.5);
    doc.text('ISSUE NO: 1  |  REVISION NO: 0', pageWidth - margin - 40, y + 8.5);
    doc.text(`DATE: ${new Date().toLocaleDateString()}`, pageWidth - margin - 40, y + 12.5);
    y += 20;
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.4); doc.line(margin, y, pageWidth - margin, y);
    y += 7;
    doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 30, 30);
    doc.text('FUEL REQUISITION FORM', pageWidth / 2, y, { align: 'center' });
    doc.setDrawColor(30, 64, 175); doc.setLineWidth(0.8); doc.line(65, y + 2, 145, y + 2);
    y += 9;
    doc.setDrawColor(180, 180, 180); doc.setFillColor(247, 247, 247); doc.setLineWidth(0.3);
    doc.rect(margin, y, pageWidth - 2 * margin, 9, 'FD');
    doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(60, 60, 60);
    doc.text('CONTROL #:', margin + 3, y + 6);
    doc.setFontSize(9); doc.setTextColor(30, 64, 175);
    doc.text(request.controlNo || '—', margin + 27, y + 6);
    y += 13;
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('REQUESTOR INFORMATION', margin, y);
    doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.3); doc.line(margin, y + 1.5, pageWidth - margin, y + 1.5);
    y += 5;
    const lCol = margin + 2; const rCol = pageWidth / 2 + 5;
    const drawInfoField = (label, value, x, fieldY) => {
      doc.setFontSize(6.5); doc.setFont(undefined, 'normal'); doc.setTextColor(130, 130, 130); doc.text(label, x, fieldY);
      doc.setFontSize(8.5); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20); doc.text(value || '—', x, fieldY + 5);
    };
    drawInfoField('Requestor Name', request.requestorName, lCol, y);
    drawInfoField('Department', request.department || 'N/A', rCol, y);
    y += 11;
    drawInfoField('Plate Number', request.plateNumber, lCol, y);
    drawInfoField('Date & Time', `${formatDate(request.requestDate)}  |  ${formatTime(request.requestTime)}`, rCol, y);
    y += 14;
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('FUEL DETAILS', margin, y);
    doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.3); doc.line(margin, y + 1.5, pageWidth - margin, y + 1.5);
    y += 5;
    const colW = (pageWidth - 2 * margin) / 4;
    doc.setFillColor(240, 240, 240); doc.setDrawColor(210, 210, 210); doc.setLineWidth(0.2);
    doc.rect(margin, y, pageWidth - 2 * margin, 7, 'FD');
    doc.setFontSize(6.5); doc.setFont(undefined, 'bold'); doc.setTextColor(70, 70, 70);
    doc.text('DESCRIPTION', margin + 3, y + 5); doc.text('LEVEL', margin + colW + 3, y + 5);
    doc.text('ACTUAL LITERS', margin + colW * 2 + 3, y + 5); doc.text('TRUCK ODOMETER', margin + colW * 3 + 3, y + 5);
    y += 7;
    doc.setFillColor(252, 252, 252); doc.setDrawColor(210, 210, 210); doc.rect(margin, y, pageWidth - 2 * margin, 8, 'FD');
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20);
    doc.text(String(request.description || '—'), margin + 3, y + 5.5);
    doc.text(String(request.level || '—'), margin + colW + 3, y + 5.5);
    doc.text(String(request.actualLiters ?? '—'), margin + colW * 2 + 3, y + 5.5);
    doc.text(String(request.truckOdometer ?? '—'), margin + colW * 3 + 3, y + 5.5);
    y += 10;
    doc.setFillColor(247, 247, 247); doc.setDrawColor(210, 210, 210); doc.rect(margin, y, pageWidth - 2 * margin, 14, 'FD');
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('Quantity of Liters Requested:', margin + 3, y + 5.5);
    doc.text('Flow Meter Beg:', margin + 3, y + 11); doc.text('Flow Meter End:', pageWidth / 2 + 3, y + 11);
    doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20); doc.setFontSize(8.5);
    doc.text(String(request.quantityLiters ?? '—'), margin + 57, y + 5.5);
    doc.text(String(request.flowMeterBeg ?? '—'), margin + 35, y + 11);
    doc.text(String(request.flowMeterEnd ?? '—'), pageWidth / 2 + 32, y + 11);
    y += 18;
    if (request.purpose) {
      const purposeLines = doc.splitTextToSize(request.purpose, pageWidth - 2 * margin - 20);
      const purposeH = Math.max(12, purposeLines.length * 5 + 8);
      doc.setFillColor(247, 247, 247); doc.setDrawColor(210, 210, 210); doc.setLineWidth(0.2);
      doc.rect(margin, y, pageWidth - 2 * margin, purposeH, 'FD');
      doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
      doc.text('PURPOSE:', margin + 3, y + 5.5);
      doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20); doc.setFontSize(7.5);
      doc.text(purposeLines, margin + 22, y + 5.5);
      y += purposeH + 6;
    }
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('APPROVALS & SIGNATURES', margin, y);
    doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.3); doc.line(margin, y + 1.5, pageWidth - margin, y + 1.5);
    y += 6;
    const sigW = (pageWidth - 2 * margin - 5) / 2; const sigH = 22;
    const drawSigBox = (x, boxY, label, name, disabled = false) => {
      if (disabled) { doc.setFillColor(245, 245, 245); } else { doc.setFillColor(255, 255, 255); }
      doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.25); doc.rect(x, boxY, sigW, sigH, 'FD');
      doc.setFontSize(6.5); doc.setFont(undefined, 'bold');
      doc.setTextColor(disabled ? 160 : 80, disabled ? 160 : 80, disabled ? 160 : 80);
      doc.text(label, x + 3, boxY + 5);
      if (disabled) {
        doc.setFontSize(7.5); doc.setFont(undefined, 'italic'); doc.setTextColor(170, 170, 170);
        doc.text('N/A — Not Required', x + sigW / 2, boxY + 14, { align: 'center' });
      } else {
        doc.setDrawColor(100, 100, 100); doc.setLineWidth(0.35); doc.line(x + 3, boxY + 16, x + sigW - 3, boxY + 16);
        if (name) { doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20); doc.text(name, x + 3, boxY + 14); }
        doc.setFontSize(5.5); doc.setFont(undefined, 'italic'); doc.setTextColor(140, 140, 140);
        doc.text('Signature & Date', x + 3, boxY + 20);
      }
    };
    drawSigBox(margin, y, 'REQUESTED BY', request.requestedBy || request.requestorName);
    drawSigBox(margin + sigW + 5, y, 'NOTED BY', request.notedBy);
    y += sigH + 4;
    drawSigBox(margin, y, 'APPROVED BY', request.approvedBy);
    drawSigBox(margin + sigW + 5, y, 'GAS MAN / ASSIST BY', request.gasManAssistBy);
    y += sigH + 6;
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3); doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
    doc.setFontSize(6); doc.setFont(undefined, 'italic'); doc.setTextColor(150, 150, 150);
    doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    doc.text(`Control No: ${request.controlNo}  |  SPR-F-009`, pageWidth / 2, pageHeight - 4, { align: 'center' });
    doc.save(`Fuel_Requisition_${request.controlNo}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRequests = filteredRequests.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-6">
      <div className="max-w-8xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl shadow-lg shadow-blue-200">
                <Fuel className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Fuel Requisition List</h1>
                <p className="text-gray-500 mt-1">View and manage all fuel requests</p>
              </div>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by Control No, Requestor, Plate #, Department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Loading fuel requests...</p>
              </div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-20">
              <Fuel className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">No fuel requests found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Control No.</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Requestor</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Plate #</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Department</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Qty (L)</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">ODO</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {currentRequests.map((request) => {
                      const approved = isApproved(request);
                      return (
                        <tr key={request.id} className="hover:bg-blue-50 transition-colors">
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-blue-200">
                              <FileText className="w-3.5 h-3.5" />
                              {request.controlNo}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-gray-900">{formatDate(request.requestDate)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-gray-900">{request.requestorName}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-mono font-bold text-gray-900">{request.plateNumber}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium text-gray-700">{request.department || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-blue-200">
                              <Fuel className="w-3.5 h-3.5" />
                              {request.quantityLiters || 0} L
                            </span>
                          </td>

                          {/* ODO Defective badge */}
                          <td className="px-6 py-4 text-center">
                            {request.odo_Defective ? (
                              <div className="flex flex-col items-center gap-1">
                                <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2.5 py-1 rounded-lg text-xs font-bold border border-red-200">
                                  ⚠️ Defective
                                </span>
                                {request.odo_DefectiveNo && (
                                  <span className="text-xs text-gray-400 font-mono">{request.odo_DefectiveNo}</span>
                                )}
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 px-2.5 py-1 rounded-lg text-xs font-medium border border-gray-200">
                                OK
                              </span>
                            )}
                          </td>

                          {/* ✅ Status — based sa approvedBy */}

                          {/* ✅ Status — based sa approvedBy */}
                          <td className="px-6 py-4 text-center">
                            {approved ? (
                              <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-green-200">
                                <Lock className="w-3 h-3" /> Approved
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 bg-yellow-100 text-yellow-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-yellow-200">
                                Pending
                              </span>
                            )}
                          </td>

                          {/* ✅ Actions */}
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-2">

                              <button
                                onClick={() => { setFeedFuel(request); setShowFeedModal(true); }}
                                className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl transition-all shadow-md text-xs font-semibold"
                                title="Activity Feed"
                              >
                                <MessageCircle className="w-3.5 h-3.5" /> Feed
                              </button>
                              {/* View — lagi available */}
                              <button
                                onClick={() => handleViewRequest(request)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all shadow-md text-xs font-semibold"
                                title="View"
                              >
                                <Eye className="w-3.5 h-3.5" /> View
                              </button>

                              {/* Edit */}
                              <button
                                onClick={() => handleEditRequest(request)}
                                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl transition-all shadow-md text-xs font-semibold
                                  ${approved
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                    : 'bg-amber-500 hover:bg-amber-600 text-white'
                                  }`}
                                title={approved ? 'Cannot edit — already approved' : 'Edit'}
                              >
                                {approved ? <Lock className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                                Edit
                              </button>

                              {/* Delete */}
                              <button
                                onClick={() => handleDeleteRequest(request)}
                                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl transition-all shadow-md text-xs font-semibold
                                  ${approved
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                    : 'bg-red-500 hover:bg-red-600 text-white'
                                  }`}
                                title={approved ? 'Cannot delete — already approved' : 'Delete'}
                              >
                                {approved ? <Lock className="w-3.5 h-3.5" /> : <Trash2 className="w-3.5 h-3.5" />}
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-bold text-gray-900">{startIndex + 1}</span> to{' '}
                    <span className="font-bold text-gray-900">{Math.min(endIndex, filteredRequests.length)}</span> of{' '}
                    <span className="font-bold text-gray-900">{filteredRequests.length}</span> requests
                  </p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1">
                      <ChevronLeft className="w-4 h-4" /> Previous
                    </button>
                    <span className="text-sm font-medium px-4">{currentPage} / {totalPages}</span>
                    <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1">
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ─── VIEW MODAL (UNCHANGED) ─── */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-700 to-blue-800 text-white px-8 py-6 flex-shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-white/20 p-2 rounded-lg"><FileText className="w-6 h-6" /></div>
                    <h2 className="text-2xl font-bold">Fuel Requisition Form</h2>
                  </div>
                  <div className="bg-white/10 inline-block px-4 py-1.5 rounded-lg border border-white/20">
                    <p className="text-sm">Control No: <span className="font-bold text-base ml-2">{selectedRequest.controlNo}</span></p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => exportToPDF(selectedRequest)} className="p-2.5 hover:bg-white/20 rounded-lg transition-all border border-white/20" title="Download PDF">
                    <Download className="h-5 w-5" />
                  </button>
                  <button onClick={() => setShowModal(false)} className="p-2.5 hover:bg-white/20 rounded-lg transition-all border border-white/20">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
            <div className="p-8 bg-gray-50 flex-1 overflow-y-auto">
              <div className="bg-white border-2 border-blue-100 rounded-xl p-6 mb-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base font-bold text-gray-900">Requestor Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Requestor Name</p><p className="text-base font-bold text-gray-900">{selectedRequest.requestorName}</p></div>
                  <div><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Plate Number</p><p className="text-base font-bold text-gray-900 font-mono">{selectedRequest.plateNumber}</p></div>
                  <div><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Department</p><p className="text-base font-bold text-gray-900">{selectedRequest.department || 'N/A'}</p></div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Date & Time</p>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-600" />
                      <p className="text-base font-bold text-gray-900">{formatDate(selectedRequest.requestDate)} <span className="text-blue-500">•</span> {formatTime(selectedRequest.requestTime)}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Fuel className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base font-bold text-gray-900">Fuel Details</h3>
                </div>
                <div className="bg-blue-700 text-white rounded-xl p-4 mb-4 shadow-md">
                  <p className="text-sm font-semibold opacity-80">Quantity Requested</p>
                  <p className="text-3xl font-bold">{selectedRequest.quantityLiters || 0} <span className="text-xl font-normal">LITERS</span></p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200"><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Description</p><p className="text-sm font-bold text-gray-900">{selectedRequest.description || 'N/A'}</p></div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200"><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Level</p><p className="text-sm font-bold text-gray-900">{selectedRequest.level || 'N/A'}</p></div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200"><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Actual Liters</p><p className="text-sm font-bold text-gray-900">{selectedRequest.actualLiters || 0} L</p></div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Truck Odometer</p>
                    {selectedRequest.odo_Defective ? (
                      <div className="flex flex-col gap-1">
                        <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-700 px-2.5 py-1 rounded-lg text-xs font-bold border border-red-200 w-fit">
                          ⚠️ ODO Defective
                        </span>
                        {selectedRequest.odo_DefectiveNo && (
                          <span className="text-xs text-gray-500 font-mono">Ref: {selectedRequest.odo_DefectiveNo}</span>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm font-bold text-gray-900">{selectedRequest.truckOdometer || 0}</p>
                    )}
                  </div>                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 col-span-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Flow Meter</p>
                    <div className="flex items-center gap-4">
                      <div><span className="text-xs text-gray-500">Beg:</span><span className="text-sm font-bold text-gray-900 ml-1">{selectedRequest.flowMeterBeg || 0}</span></div>
                      <div className="h-5 w-px bg-gray-300"></div>
                      <div><span className="text-xs text-gray-500">End:</span><span className="text-sm font-bold text-gray-900 ml-1">{selectedRequest.flowMeterEnd || 0}</span></div>
                    </div>
                  </div>
                </div>
              </div>
              {selectedRequest.purpose && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 mb-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-blue-700" />
                    <h3 className="text-sm font-bold text-blue-900 uppercase">Purpose</h3>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed">{selectedRequest.purpose}</p>
                </div>
              )}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-base font-bold text-gray-900 mb-4">Approvals & Signatures</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {[
                    { label: 'Requested By', value: selectedRequest.requestedBy },
                    { label: 'Noted By', value: selectedRequest.notedBy },
                    { label: 'Approved By', value: selectedRequest.approvedBy },
                    { label: 'Gas Man / Assist By', value: selectedRequest.gasManAssistBy },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-3">{label}</p>
                      <div className="border-b-2 border-blue-700 pb-3 mb-2">
                        <p className="text-sm font-bold text-gray-900">{value || '—'}</p>
                      </div>
                      <p className="text-xs text-gray-400 italic">Signature Over Printed Name</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="border-t bg-gray-50 px-8 py-5 flex justify-end gap-3 flex-shrink-0">

              <button onClick={() => exportToPDF(selectedRequest)} className="px-6 py-3 bg-blue-700 hover:bg-blue-800 text-white rounded-xl font-semibold transition-all shadow-lg flex items-center gap-2">
                <Printer className="w-4 h-4" /> Export PDF
              </button>
              <button onClick={() => setShowModal(false)} className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-semibold transition-all shadow-md">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── EDIT MODAL ─── */}
      {showEditModal && editData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-8 py-6 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg"><Pencil className="w-6 h-6" /></div>
                  <div>
                    <h2 className="text-2xl font-bold">Edit Fuel Requisition</h2>
                    <p className="text-amber-100 text-sm mt-0.5">Control No: {editData.controlNo}</p>
                  </div>
                </div>
                <button onClick={() => { setShowEditModal(false); setEditData(null); }} className="p-2.5 hover:bg-white/20 rounded-lg transition-all border border-white/20">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-8 flex-1 overflow-y-auto bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {[
                  { label: 'Requestor Name *', key: 'requestorName', type: 'text' },
                  { label: 'Plate Number *', key: 'plateNumber', type: 'text' },
                  { label: 'Department *', key: 'department', type: 'text' },
                  { label: 'Request Date', key: 'requestDate', type: 'date' },
                  { label: 'Description', key: 'description', type: 'text' },
                  { label: 'Level', key: 'level', type: 'text' },
                  { label: 'Actual Liters', key: 'actualLiters', type: 'number' },
                  { label: 'Quantity Liters', key: 'quantityLiters', type: 'number' },
                  { label: 'Truck Odometer', key: 'truckOdometer', type: 'number' },
                  { label: 'Flow Meter Beg', key: 'flowMeterBeg', type: 'number' },
                  { label: 'Flow Meter End', key: 'flowMeterEnd', type: 'number' },
                  { label: 'Requested By', key: 'requestedBy', type: 'text' },
                  { label: 'Noted By', key: 'notedBy', type: 'text' },
                  { label: 'Gas Man / Assist By', key: 'gasManAssistBy', type: 'text' },
                ].map(({ label, key, type }) => (
                  <div key={key}>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{label}</label>
                    <input
                      type={type}
                      value={key === 'requestDate' && editData[key] ? editData[key].split('T')[0] : editData[key] ?? ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, [key]: e.target.value }))}
                      className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-400 focus:outline-none text-sm transition-all"
                    />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Purpose</label>
                  <textarea
                    value={editData.purpose ?? ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, purpose: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-400 focus:outline-none text-sm transition-all resize-none"
                  />
                </div>
              </div>
            </div>
            <div className="border-t bg-gray-50 px-8 py-5 flex justify-end gap-3 flex-shrink-0">


              <button onClick={() => { setShowEditModal(false); setEditData(null); }}
                className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold transition-all shadow-md flex items-center gap-2">
                <XCircle className="w-4 h-4" /> Cancel
              </button>
              <button onClick={handleEditSave} disabled={editLoading}
                className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold transition-all shadow-lg flex items-center gap-2 disabled:opacity-60">
                {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showFeedModal && feedFuel && (
        <ActivityFeedModal_Fuel
          fuel={feedFuel}
          onClose={() => { setShowFeedModal(false); setFeedFuel(null); }}
        />
      )}
    </div>
  );
}